// pendingPlan.ts
// Persists the plan a visitor picked on the welcome page across the auth flow.
// localStorage (not sessionStorage) so it survives the email-verify new-tab handoff
// and the full-page OAuth redirect on mobile.
//
// Envelope shape `{ planId, ts }` with a 30-minute TTL — prevents stale plans from
// hijacking a future login session if the user abandons payment and returns weeks
// later.

export type PlanId = 'FULL_ACCESS_MONTHLY' | 'FULL_ACCESS_YEARLY'

const KEY = 'dhtn_pending_plan'
const TTL_MS = 30 * 60 * 1000

const VALID_PLANS: ReadonlySet<PlanId> = new Set<PlanId>([
    'FULL_ACCESS_MONTHLY',
    'FULL_ACCESS_YEARLY',
])

interface Envelope {
    planId: PlanId
    ts: number
}

const isValidPlan = (value: unknown): value is PlanId =>
    typeof value === 'string' && VALID_PLANS.has(value as PlanId)

export const setPendingPlan = (planId: PlanId): void => {
    const envelope: Envelope = { planId, ts: Date.now() }
    try {
        localStorage.setItem(KEY, JSON.stringify(envelope))
    } catch {
        // Storage quota exceeded or disabled — silently drop.
        // The flow degrades to "user lands on /dashboard after signup", same as no-plan flow.
    }
}

/**
 * Returns the pending plan if one is set, valid, and fresh (≤30 min old).
 * Clears the entry as a side-effect when it's stale, corrupt, or invalid —
 * so callers never see the same broken value twice.
 */
export const getPendingPlan = (): PlanId | null => {
    let raw: string | null
    try {
        raw = localStorage.getItem(KEY)
    } catch {
        // With storage disabled the learner can choose a plan again on pricing.
        return null
    }
    if (!raw) return null

    try {
        const parsed: unknown = JSON.parse(raw)
        if (
            typeof parsed === 'object' &&
            parsed !== null &&
            'planId' in parsed &&
            'ts' in parsed
        ) {
            const { planId, ts } = parsed as { planId: unknown; ts: unknown }
            if (
                isValidPlan(planId) &&
                typeof ts === 'number' &&
                Date.now() - ts <= TTL_MS
            ) {
                return planId
            }
        }
    } catch {
        // Fall through to cleanup below.
    }

    // Stale, corrupt, or invalid — drop it so the next caller gets a clean read.
    clearPendingPlan()
    return null
}

export const clearPendingPlan = (): void => {
    try {
        localStorage.removeItem(KEY)
    } catch {
        // Disabled browser storage must not interrupt payment confirmation.
    }
}
